import { useState } from 'react';
import * as Sentry from '@sentry/react';
import { Action, Button, TextField, MiniAlert, FlexCol } from '@veeqo/ui';
import { useForm, Controller } from 'react-hook-form';
import { verifyBackupCode } from 'utils/api';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from 'context/Context';
import { useTrackForm } from 'utils/adobeAnalytics/hooks/useTrackForm';
import { SESSION_EXPIRED_MESSAGE } from 'utils/constants';
import { appendRemainingCodesParam } from 'utils/appendRemainingCodesParam';
import { Form } from '../styled';

type FormData = {
    backupCode: string;
};

type Response = {
    error?: boolean;
    sessionExpired?: boolean;
    redirectTo?: string;
    backupCodesRemaining?: number;
};

export const BackupCodeForm = () => {
    const { state } = useAppContext();
    const navigate = useNavigate();
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<FormData>();

    const [hasVerificationFailed, setHasVerificationFailed] = useState(false);
    const [isSessionExpired, setIsSessionExpired] = useState(false);
    const [isButtonLoading, setIsButtonLoading] = useState(false);

    const { trackFormUpdated } = useTrackForm('signin-backup-code-form');

    const onSubmit = handleSubmit(async ({ backupCode }) => {
        setIsButtonLoading(true);
        setHasVerificationFailed(false);
        const returnTo = state.signupData.return_to;
        const response: Response | undefined = await verifyBackupCode({
            backup_code: backupCode,
            return_to: returnTo,
        });

        // The verification session has expired: the user can't retry here. Lock the form (disable
        // every action except "Back to login") and show a persistent error, rather than silently
        // redirecting them.
        if (response?.sessionExpired) {
            setIsButtonLoading(false);
            setIsSessionExpired(true);
            return;
        }

        // Backup codes are single-use. On any other failure we keep the user on this screen so they
        // can try another saved code rather than kicking them back to the login form.
        if (response?.error) {
            setIsButtonLoading(false);
            setHasVerificationFailed(true);
            return;
        }

        if (response?.redirectTo) {
            const target = decodeURIComponent(response.redirectTo);
            global.location.assign(appendRemainingCodesParam(target, response.backupCodesRemaining));
        } else {
            setIsButtonLoading(false);
            Sentry.captureMessage('No redirect_to when verifying backup code');
        }
    });

    return (
        <Form onSubmit={onSubmit} id="signin-backup-code-form">
            <FlexCol alignItems="stretch" gap="xl">
                <FlexCol>
                    <Controller
                        name="backupCode"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                            <TextField
                                onChange={onChange}
                                onBlur={trackFormUpdated('backupCode')}
                                value={value}
                                aria-label="backup code"
                                type="text"
                                label="Backup code"
                                placeholder="xxxxx-xxxxx"
                                autoComplete="one-time-code"
                                error={errors.backupCode && errors.backupCode.message}
                            />
                        )}
                        rules={{ required: 'Required' }}
                    />
                    {isSessionExpired ? (
                        <MiniAlert variant="error" title={SESSION_EXPIRED_MESSAGE} />
                    ) : (
                        hasVerificationFailed && (
                            <MiniAlert
                                variant="error"
                                title="Invalid backup code. Please try again, or use another one."
                            />
                        )
                    )}
                </FlexCol>
                <FlexCol>
                    <Button
                        type="submit"
                        variant="primary"
                        loading={isButtonLoading}
                        disabled={isButtonLoading || isSessionExpired}
                    >
                        Verify
                    </Button>
                    <Action
                        variant="flat"
                        disabled={isSessionExpired}
                        onClick={() => navigate('..')}
                    >
                        Use authenticator app instead
                    </Action>
                </FlexCol>
            </FlexCol>
        </Form>
    );
};
